import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NzTableModule } from "ng-zorro-antd/table";
import { ApiService } from "@/modules//dashboard/infra/api.service";
import { DashboardData, DashboardResponse } from "@/modules//dashboard/core/types";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

interface UserRanking {
  name: string;
  done: number;
  in_progress: number;
  canceled: number;
  total: number;
}

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzButtonModule,
    NzCheckboxModule,
    NzDropDownModule,
    NzIconModule,
    NzInputModule,
    NzToolTipModule
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss'
})
export class TableComponent implements OnInit, OnDestroy {
  @Input() isMonitorMode = false;
  listOfData: UserRanking[] = [];
  displayedData: UserRanking[] = [];
  rawItems: DashboardData[] = [];

  // Manual Pagination
  currentPage = 1;
  currentPageSize = 10;
  private autoPageInterval: any;

  // Type filter properties
  typeSearch = '';
  visibleTypeFilters: { text: string; value: any; checked: boolean }[] = [];
  typeFilters: { text: string; value: any; checked: boolean }[] = [];

  constructor(private dashboardService: ApiService) {}

  ngOnInit(): void {
    if (this.isMonitorMode) {
      this.currentPageSize = 12;
      this.startAutoPaging();
    }
    this.getApiData();
  }

  ngOnDestroy(): void {
    if (this.autoPageInterval) {
      clearInterval(this.autoPageInterval);
    }
  }

  private startAutoPaging() {
    this.autoPageInterval = setInterval(() => {
      if (this.listOfData.length > this.currentPageSize) {
        const totalPages = Math.ceil(this.listOfData.length / this.currentPageSize);
        this.currentPage = (this.currentPage % totalPages) + 1;
        this.updateDisplayedData();
      }
    }, 10000);
  }

  updateDisplayedData() {
    if (!this.isMonitorMode) {
      this.displayedData = [...this.listOfData];
      return;
    }
    const startIndex = (this.currentPage - 1) * this.currentPageSize;
    const endIndex = startIndex + this.currentPageSize;
    this.displayedData = this.listOfData.slice(startIndex, endIndex);
  }

  getApiData() {
    this.dashboardService.findAll().subscribe({
      next: (data: DashboardResponse<DashboardData>) => {
        this.rawItems = data.items;
        this.generateFilters();
        this.processData(this.rawItems);
      },
      error: (err: Error) => {
        console.error(err);
      }
    })
  }

  processData(items: DashboardData[]) {
    const userStats: Record<string, UserRanking> = {};

    items.forEach(item => {
      const userName = item.user_name;

      if (!userStats[userName]) {
        userStats[userName] = {
          name: userName,
          done: 0,
          in_progress: 0,
          canceled: 0,
          total: 0
        };
      }

      if (item.status === 'done') {
        userStats[userName].done++;
      } else if (item.status === 'in_progress') {
        userStats[userName].in_progress++;
      } else if (item.status === 'canceled') {
        userStats[userName].canceled++;
      }
      
      userStats[userName].total++;
    });

    this.listOfData = Object.values(userStats).sort((a, b) => b.total - a.total);
    this.updateDisplayedData();
  }

  generateFilters() {
    const uniqueTypes = new Set(this.rawItems.map(item => item.type));
    this.typeFilters = Array.from(uniqueTypes).map(type => ({ text: this.setTypeName(type), value: type, checked: false }));
    this.visibleTypeFilters = [...this.typeFilters];
  }

  onTypeSearch(searchValue: string) {
    this.typeSearch = searchValue;
    this.visibleTypeFilters = this.typeFilters.filter(item => item.text.toLowerCase().includes(searchValue.toLowerCase()));
  }

  applyTypeFilter() {
    const selectedTypes = this.typeFilters.filter(item => item.checked).map(item => item.value);
    
    if (selectedTypes.length > 0) {
      const filteredItems = this.rawItems.filter(item => selectedTypes.includes(item.type));
      this.processData(filteredItems);
    } else {
      this.processData(this.rawItems);
    }
  }

  clearTypeFilter() {
    this.typeSearch = '';
    this.typeFilters.forEach(item => item.checked = false);
    this.visibleTypeFilters = [...this.typeFilters];
    this.processData(this.rawItems);
  }

  get isTypeFilterActive(): boolean {
    return this.typeFilters.some(item => item.checked);
  }

  setTypeName (type: string) {
    switch (type) {
      case 'new':
        return 'Novo'
      case 'used':
        return 'Usado'
      case 'transfer_start':
        return 'Transferência enviada'
      case 'transfer_done':
        return 'Transferência recebida'
      case 'vehicle':
        return 'Veículo'
      case 'maintenance':
        return 'Manutenção'
      default:
        return ''
    }
  }
}
